import { useCallback, useMemo, useState } from 'react'
import { useSetRarities } from '@/hooks/collections/useSetRarities'
import { useHideRarity, useHideSet, useUnhideRarity, useUnhideSet } from '@/hooks/filters/useUserFilters'
import { useFilterStore } from '@/store/filterStore'
import type { CollectionStats, RarityProgress, SetProgress } from '@/types'

type SetSortBy = 'set_order' | 'percentage' | 'collected'

type PlaysetModalMode = 'buylist_missing' | 'selllist_extras'

interface PlaysetModalState {
  setCode: string
  setName: string
  mode: PlaysetModalMode
}

/** Pure sort of set-progress rows by the selected mode (does not mutate input). */
function sortSets(sets: SetProgress[] | undefined, sortBy: SetSortBy): SetProgress[] {
  if (!sets) return []
  switch (sortBy) {
    case 'percentage':
      return [...sets].sort((a, b) => b.percentage - a.percentage)
    case 'collected':
      return [...sets].sort((a, b) => b.collected - a.collected)
    case 'set_order':
    default:
      return sets
  }
}

/**
 * Owns all Set-Collection-Progress interaction state: sort order, the expanded
 * set (and its lazily-derived rarity breakdown), the four hide/unhide filter
 * mutations, and the playset-modal target. Keeps `SetProgressTile` presentational.
 */
export function useSetProgress(stats: CollectionStats | undefined) {
  const { showHidden, collapseVariants } = useFilterStore()
  const hideSet = useHideSet()
  const unhideSet = useUnhideSet()
  const hideRarity = useHideRarity()
  const unhideRarity = useUnhideRarity()

  const [sortBy, setSortBy] = useState<SetSortBy>('set_order')
  const [expandedSet, setExpandedSet] = useState<string | null>(null)
  const [playsetModal, setPlaysetModal] = useState<PlaysetModalState | null>(null)

  const { data: rarityData, isLoading: rarityLoading } = useSetRarities(expandedSet, showHidden, collapseVariants)

  const sets = useMemo(() => sortSets(stats?.setProgress, sortBy), [stats?.setProgress, sortBy])

  const toggleExpanded = useCallback(
    (setCode: string) => setExpandedSet((current) => (current === setCode ? null : setCode)),
    [],
  )

  const toggleSetHidden = useCallback(
    (set: SetProgress) => {
      if (set.isHidden) unhideSet.mutate(set.setCode)
      else hideSet.mutate(set.setCode)
    },
    [hideSet, unhideSet],
  )

  const toggleRarityHidden = useCallback(
    (setCode: string, rarity: RarityProgress) => {
      if (rarity.isHidden) unhideRarity.mutate({ setCode, rarity: rarity.rarity })
      else hideRarity.mutate({ setCode, rarity: rarity.rarity })
    },
    [hideRarity, unhideRarity],
  )

  const openPlaysetModal = useCallback((setCode: string, setName: string, mode: PlaysetModalMode) => {
    setPlaysetModal({ setCode, setName, mode })
  }, [])

  const closePlaysetModal = useCallback(() => setPlaysetModal(null), [])

  return {
    sets,
    sortBy,
    setSortBy,
    expandedSet,
    toggleExpanded,
    rarityData,
    rarityLoading,
    toggleSetHidden,
    toggleRarityHidden,
    playsetModal,
    openPlaysetModal,
    closePlaysetModal,
  }
}
